/**
 * Helpers for the club stand enamel sheet (tag-in + stand-entry log).
 */

import {
  degreesToEightCardinal,
  getOptimalWindsArray,
  isStandOptimalForWind,
} from './windUtils';

export const WIND_DIRECTION_LABELS = {
  N: 'North',
  NE: 'Northeast',
  E: 'East',
  SE: 'Southeast',
  S: 'South',
  SW: 'Southwest',
  W: 'West',
  NW: 'Northwest',
};

export function formatOptimalWindsLabel(location, emptyLabel = 'No Preference') {
  const winds = getOptimalWindsArray(location);
  if (!winds.length) return emptyLabel;
  return winds.map((w) => WIND_DIRECTION_LABELS[w] || w).join(', ');
}

function resolveTagInWeather(outlookDay, huntWeather, sitPeriod) {
  if (huntWeather && sitPeriod) {
    const pw = huntWeather[String(sitPeriod).toLowerCase()];
    if (pw) return pw;
  }
  return outlookDay?.weather || null;
}

function resolveWindDegrees(weather) {
  if (!weather) return null;
  if (typeof weather.wind_deg === 'number') return weather.wind_deg;
  if (typeof weather.wind_direction === 'number') return weather.wind_direction;
  return null;
}

/**
 * Compact wind + score snapshot for the tag-in mustard card.
 * Shows live wind (speed + 8-point cardinal), not the stand's wind list.
 */
export function buildTagInConditionsCard({
  location,
  outlookDay = null,
  huntWeather = null,
  sitPeriod = null,
  outlookLoading = false,
} = {}) {
  const weather = resolveTagInWeather(outlookDay, huntWeather, sitPeriod);
  const windDeg = resolveWindDegrees(weather);
  const speed =
    weather && typeof weather.wind_speed === 'number'
      ? Math.round(weather.wind_speed)
      : null;
  let cardinal = windDeg != null ? degreesToEightCardinal(windDeg) : null;
  if (!cardinal && weather?.wind_direction_text) {
    cardinal = String(weather.wind_direction_text).trim().toUpperCase();
  }

  const windLabel =
    speed != null && cardinal
      ? `${speed} ${cardinal}`
      : speed != null
        ? String(speed)
        : cardinal || null;

  const isOptimal =
    windDeg != null
      ? isStandOptimalForWind(location, windDeg)
      : Boolean(cardinal && getOptimalWindsArray(location).includes(cardinal));

  const scoreRaw = Number(outlookDay?.score);
  const score = Number.isFinite(scoreRaw) ? Math.round(scoreRaw) : null;

  return {
    windLabel,
    isOptimal,
    score,
    loading: Boolean(outlookLoading) && windLabel == null && score == null,
  };
}

export function resolveHuntLocationNumericId(location) {
  const getNumericId = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value);
    const m = str.match(/(\d+)$/);
    return m ? Number(m[1]) : null;
  };
  const candidates = [
    location?.properties?.id,
    location?.id,
    location?.properties?.locationId,
    location?.locationId,
  ];
  for (const c of candidates) {
    const n = getNumericId(c);
    if (n != null) return n;
  }
  return null;
}

export function resolveStandCoordinates(location) {
  const geo =
    location?.geojson ||
    location?.geometry ||
    location?.properties?.geojson ||
    location?.location?.geojson;
  if (geo?.type === 'Point' && Array.isArray(geo.coordinates) && geo.coordinates.length >= 2) {
    return { lng: geo.coordinates[0], lat: geo.coordinates[1] };
  }
  return null;
}

export function standNameFromLocation(location) {
  return (
    location?.name ||
    location?.properties?.name ||
    location?.location?.name ||
    'Hunt Location'
  );
}

/**
 * Build create-hunt payload for stand-entry log (3-step sheet).
 * Turkeys are UI-only in the handoff; API has no turkey field — omitted.
 */
export function buildStandLogPayload({
  date,
  timePeriod,
  locationName,
  clubId,
  huntLocationId,
  saw,
  harvest,
  weight,
  points,
  notes,
  weatherData = {},
}) {
  const hasHarvest = harvest === 'buck' || harvest === 'doe';
  const payload = {
    date,
    time_periods: timePeriod ? [String(timePeriod).toLowerCase()] : [],
    location: locationName,
    location_type: 'club_location',
    club_id: clubId != null ? Number(clubId) : null,
    hunt_location_id: huntLocationId != null ? Number(huntLocationId) : null,
    does_seen: Number(saw?.Does) || 0,
    bucks_seen: Number(saw?.Bucks) || 0,
    fawns_seen: Number(saw?.Fawns) || 0,
    shot_taken: hasHarvest,
    harvest: hasHarvest,
    notes: notes || '',
    weather_data: weatherData || {},
  };

  if (hasHarvest) {
    payload.harvest_details = {
      deer_type: harvest,
      method: 'gun',
      weight: weight ? parseFloat(weight) : null,
      points: harvest === 'buck' && points !== '' && points != null ? parseInt(points, 10) : null,
      notable_features: '',
    };
  }

  return payload;
}

export function buildOccupancyFromStandRow(standRow, currentUserId, date) {
  if (!standRow?.isOccupied) return null;
  const occupiedByUserId = standRow.occupiedByUserId ?? null;
  const isMine =
    currentUserId != null &&
    occupiedByUserId != null &&
    Number(occupiedByUserId) === Number(currentUserId);
  return {
    isOccupied: true,
    isMine,
    occupiedBy: standRow.occupiedBy || null,
    occupiedByUserId,
    timePeriod: standRow.timePeriod || null,
    date,
  };
}

export function buildOccupancyFromSignOut(signOutDetails, currentUserId, date) {
  if (!signOutDetails) return null;
  const occupiedByUserId = signOutDetails.user_id ?? null;
  const isMine =
    currentUserId != null &&
    occupiedByUserId != null &&
    Number(occupiedByUserId) === Number(currentUserId);
  return {
    isOccupied: true,
    isMine,
    occupiedBy: signOutDetails.user_name || null,
    occupiedByUserId,
    timePeriod: signOutDetails.time_period || null,
    date: signOutDetails.date || date,
  };
}

/**
 * Build enamel condition rows for stand-entry log step 3.
 * Prefers hunt-weather shape from weatherService.getHuntWeatherData (per period),
 * which matches the legacy HuntLogPage preview fields.
 */
export function buildStandConditionRows({
  standName,
  sitPeriod,
  date,
  huntWeather = null,
  outlookWeather = null,
}) {
  const rows = [];
  const periodKey = sitPeriod ? String(sitPeriod).toLowerCase() : null;
  const periodLabel = sitPeriod
    ? String(sitPeriod).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

  if (standName) rows.push({ k: 'STAND', v: standName });
  if (periodLabel) rows.push({ k: 'SIT', v: periodLabel });
  if (date) rows.push({ k: 'DATE', v: date });

  const periodWx =
    huntWeather && periodKey && huntWeather[periodKey]
      ? huntWeather[periodKey]
      : null;

  if (periodWx) {
    if (periodWx.temperature != null) {
      rows.push({ k: 'TEMP', v: `${Math.round(periodWx.temperature)}°F` });
    }
    const windBits = [
      periodWx.wind_speed != null ? `${Math.round(periodWx.wind_speed)} mph` : null,
      periodWx.wind_direction_text || null,
    ].filter(Boolean);
    if (windBits.length) rows.push({ k: 'WIND', v: windBits.join(' ') });
    if (periodWx.humidity != null) {
      rows.push({ k: 'HUMIDITY', v: `${Math.round(periodWx.humidity)}%` });
    }
    if (periodWx.description || periodWx.main) {
      rows.push({ k: 'CONDITIONS', v: String(periodWx.description || periodWx.main) });
    }
    return rows;
  }

  // Fallback: optimal_days outlook payload (thinner / different keys)
  const w = outlookWeather || {};
  if (w.temp != null || w.temperature != null) {
    rows.push({ k: 'TEMP', v: `${Math.round(w.temp ?? w.temperature)}°F` });
  }
  const windDeg = typeof w.wind_deg === 'number' ? w.wind_deg : null;
  const windSpeed = typeof w.wind_speed === 'number' ? Math.round(w.wind_speed) : null;
  const windBits = [
    windSpeed != null ? `${windSpeed} mph` : null,
    w.wind_direction_text || null,
  ].filter(Boolean);
  if (windBits.length || windDeg != null) {
    rows.push({
      k: 'WIND',
      v: windBits.length ? windBits.join(' ') : String(windDeg),
    });
  }
  if (w.humidity != null) rows.push({ k: 'HUMIDITY', v: `${Math.round(w.humidity)}%` });
  if (w.conditions || w.summary || w.description) {
    rows.push({ k: 'CONDITIONS', v: String(w.conditions || w.summary || w.description) });
  }
  if (w.pressure != null) rows.push({ k: 'PRESSURE', v: String(w.pressure) });

  return rows;
}
